import React from "react";
import { useTranslation } from "react-i18next";
import { FundRecommendationResponse } from "@rentablo/api/recommender";
import {
  Box,
  Button,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import { toRoundedNumber } from "@helpers";
import { LinkWithArrow } from "@rentablo/components";
import { CircleProgressIcon } from "@rentablo/components";
import StarIcon from "@icons/myicons/fav_star.svg?react";
import StarFilledIcon from "@icons/myicons/fav_star_filled.svg?react";
import { InfoChips } from "@components/InfoChips/InfoChips";
import { useRecommendationTags } from "../hooks/useRecommendationTags";
import { getRecommendationType } from "../utils";
import { RecommendationLogo } from "../common";

const StyledCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isWatchlisted",
})<{ isWatchlisted: boolean }>`
  display: flex;
  flex-direction: column;
  cursor: default;
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing(5, 4, 4)};
  box-shadow: 0px 1px 2px 0px #1e273229;
  background-color: ${({ isWatchlisted, theme }) =>
    isWatchlisted
      ? theme.palette.background.selectedLight
      : theme.palette.background.paper};
  border: ${({ isWatchlisted, theme }) =>
    `1px solid ${
      isWatchlisted ? theme.palette.border.active : theme.palette.border.primary
    }`};
`;

export const Recommendation = ({
  recommendation,
  images,
  onToggleWatchlist,
  isWatchlisted,
  isLoading,
  isSaving,
  onOpenDetails,
}: {
  recommendation: FundRecommendationResponse;
  images: Record<string, string | null | undefined>;
  onToggleWatchlist: (isin: FundRecommendationResponse) => void;
  isWatchlisted: boolean;
  isLoading: boolean;
  isSaving: boolean;
  onOpenDetails: (
    recommendation: FundRecommendationResponse,
    view?: "info" | "compare" | "onlyCompare"
  ) => void;
}) => {
  const theme = useTheme();
  const isLessSm = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const { tags } = useRecommendationTags(recommendation);
  const percentage = (recommendation.fund.similarity_score ?? 0) * 100;
  const percentageRounded = toRoundedNumber(percentage);

  return (
    <StyledCard isWatchlisted={isWatchlisted}>
      <Box display="flex" flexDirection="row" gap={7}>
        <Box
          display="flex"
          flexDirection="row"
          gap={2.5}
          alignItems="center"
          mb={6}
        >
          <RecommendationLogo
            imageUrl={images[recommendation.fund.isin]}
            alt={recommendation.fund.name}
          />
          <Box>
            <Typography
              variant="h6"
              sx={{
                cursor: "pointer",
              }}
              onClick={() => onOpenDetails(recommendation)}
            >
              {recommendation.fund.name}
            </Typography>
            <Typography variant="body1">
              {getRecommendationType(recommendation.fund.fund_type)}
            </Typography>
          </Box>
        </Box>
        <Box>
          {isWatchlisted ? (
            <StarFilledIcon style={{ color: theme.palette.icon.active }} />
          ) : (
            <StarIcon style={{ color: theme.palette.icon.secondary }} />
          )}
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        gap={2}
        alignItems="center"
        mb={6}
      >
        <CircleProgressIcon
          tooltipText={t("recommender.scoreTooltip")}
          percentage={percentage}
          color="primary"
          size={50}
          icon={
            <Typography variant="caption2" color="textPrimary">
              {percentageRounded}%
            </Typography>
          }
        />
        <Typography variant="body1" color="textSecondary">
          {t("recommender.matchScore", {
            score: percentageRounded,
          })}
        </Typography>
      </Box>
      <InfoChips options={tags} />
      <LinkWithArrow
        onClick={() => onOpenDetails(recommendation)}
        text={t("recommender.openProductDetails")}
        variant="body3"
        color="secondary"
        icon={<CallMadeIcon fontSize="inherit" />}
        sx={{
          margin: theme.spacing(6, 0),
        }}
      />
      <Box
        display="flex"
        flexDirection={isLessSm ? "column" : "row"}
        gap={4}
        alignItems="center"
        justifyContent={isLessSm ? "flex-start" : "flex-end"}
        width="100%"
      >
        <Button
          variant="secondary"
          fullWidth={isLessSm}
          onClick={() => onOpenDetails(recommendation, "onlyCompare")}
        >
          {t("recommender.compare")}
        </Button>
        <Button
          disabled={isLoading || isSaving}
          variant={isWatchlisted ? "tertiary" : "primary"}
          fullWidth={isLessSm}
          onClick={() => onToggleWatchlist(recommendation)}
        >
          {isWatchlisted
            ? t("recommender.removeFromWatchlist")
            : t("recommender.addToWatchlist")}
        </Button>
      </Box>
    </StyledCard>
  );
};
